import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

export const BUS_EVENT_NAME = 'biz-event-bus';
const WILDCARD = '*';

type BusEnvelope = {
	type: string;
	payload: unknown;
};

const isBusEnvelope = (obj: unknown): obj is BusEnvelope => {
	return typeof obj === 'object' && obj !== null && 'type' in obj;
};

type EventHandler = (payload: unknown, type: string) => void;

type EventContextType = {
	dispatch: (type: string, payload?: unknown) => void;
	subscribe: (type: string, handler: EventHandler) => () => void;
};

const EventContext = createContext<EventContextType | null>(null);

type BusEnvelopeDispatch = (type: string, payload?: unknown) => void;

export const dispatchBusEvent = (type: string, payload?: unknown) => {
	console.log('Dispatching event:', type, payload);
	const envelope: BusEnvelope = { type, payload };
	window.dispatchEvent(new CustomEvent(BUS_EVENT_NAME, { detail: envelope }));
};

type EventContextProviderProps = {
	children: React.ReactNode;
	customDispatch?: BusEnvelopeDispatch;
};

export const EventContextProvider = ({ children, customDispatch }: EventContextProviderProps) => {
	const listenersRef = useRef<Map<string, Set<EventHandler>>>(new Map());
	const customDispatchRef = useRef(customDispatch);
	customDispatchRef.current = customDispatch;

	const dispatch = useCallback((type: string, payload?: unknown) => {
		dispatchBusEvent(type, payload);
		customDispatchRef.current?.(type, payload);
	}, []);

	const subscribe = useCallback((type: string, handler: EventHandler) => {
		const key = type.toLowerCase();

		if (!listenersRef.current.has(key)) {
			listenersRef.current.set(key, new Set());
		}
		listenersRef.current.get(key)!.add(handler);

		return () => {
			const handlers = listenersRef.current.get(key);
			if (handlers) {
				handlers.delete(handler);
				if (handlers.size === 0) {
					listenersRef.current.delete(key);
				}
			}
		};
	}, []);

	useEffect(() => {
		const windowHandler = ((event: CustomEvent) => {
			if (!isBusEnvelope(event.detail)) return;

			console.log('Received event:', event.detail.type, event.detail.payload);

			const { type, payload } = event.detail;
			const key = type.toLowerCase();

			// Notify type-specific subscribers
			listenersRef.current.get(key)?.forEach((h) => h(payload, type));

			// Notify wildcard subscribers
			if (key !== WILDCARD) {
				listenersRef.current.get(WILDCARD)?.forEach((h) => h(payload, type));
			}
		}) as EventListener;

		window.addEventListener(BUS_EVENT_NAME, windowHandler);
		return () => window.removeEventListener(BUS_EVENT_NAME, windowHandler);
	}, []);

	return <EventContext.Provider value={{ dispatch, subscribe }}>{children}</EventContext.Provider>;
};

export const useEventContext = () => {
	const context = useContext(EventContext);
	if (!context) {
		throw new Error('useEventContext must be used within an EventContextProvider');
	}
	return context;
};

export const useOptionalEventContext = () => {
	return useContext(EventContext);
};

export const useEventSubscription = (type: string, handler: EventHandler) => {
	const { subscribe } = useEventContext();
	const handlerRef = useRef(handler);
	handlerRef.current = handler;

	useEffect(() => {
		const stableHandler: EventHandler = (payload, t) => handlerRef.current(payload, t);
		const unsubscribe = subscribe(type, stableHandler);
		return unsubscribe;
	}, [subscribe, type]);
};
